import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import PageLoader from '../../layout/components/PageLoader';
import { AUTH_GET_FEATURES_URL, NOTIFICATIONS_GET } from '../../config/api';
import { setDomainFeaturesAction, setFCMTokenToLocal, setPageRealm, setUserInfoToRedux } from './action';
import { devLog, getRealmByUrl, _can, _route, isProduction } from '../../utils';
import { fetchGetApiData } from '../../redux/action';
import { removeAuthLoadingAction } from '../../layout/action';
import SubscriptionCheck from './SubscriptionCheck';
import AccessDenied from '../../layout/AccessDenied';
import http from '../../config/http';
import { ORGANIZATION_TYPE } from '../../config/constants';
import { hasFCMToken } from '../../utils/featureCheck';

function AuthCheck({ children, publicRoute, permission, redirectTo }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const loading = useSelector(({ layout }) => layout.authLoading);
  const { user, store, org, realm } = useSelector(({ auth }) => auth);
  const { impersonate, userId } = user;
  const [redirect, setRedirect] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const { boot, shutdown } = useIntercom();

  const fetchNotifications = async () => {
    dispatch(
      fetchGetApiData({
        url: NOTIFICATIONS_GET,
        key: 'getUserNotifications',
      }),
    );
  };

  const getUserInfo = async () => {
    try {
      await setUserInfoToRedux(dispatch);
      fetchNotifications();
    } catch (e) {
      if (!publicRoute) {
        setRedirect(_route('login'));
      }
      devLog(e);
    } finally {
      dispatch(removeAuthLoadingAction());
    }
  };

  useEffect(() => {
    if (!publicRoute) {
      getUserInfo();
    } else {
      dispatch(removeAuthLoadingAction());
    }
    http.get(AUTH_GET_FEATURES_URL).then((resp) => {
      dispatch(setDomainFeaturesAction(resp.data));
    });
  }, [publicRoute]);

  useEffect(() => {
    if (realm !== getRealmByUrl(location.pathname)) {
      dispatch(setPageRealm(getRealmByUrl(location.pathname)));
    }
    if (!loading && user.userId && permission && !_can(permission)) {
      if (redirectTo) {
        setRedirect(_route(redirectTo));
      } else {
        setAccessDenied(true);
      }
    } else {
      setAccessDenied(false);
    }
  }, [location.pathname, loading, permission, user]);

  useEffect(() => {
    if (isProduction() && !impersonate) {
      if (user._id) {
        const countryCode = user.countryCode ? `+${user.countryCode} ` : '';
        const config = {
          userId: String(user._id),
          userHash: user?.intercomHash?.web,
          email: user.email,
          name: user.name,
          mobile: `${countryCode}${user.mobile}`,
          customAttributes: {
            root_user: user.rootUser,
            brand_name: store?.name,
            amazon_ads_connected: Boolean(store?.amazonAdsConnected),
            amazon_sp_connected: Boolean(store?.amazonSPConnected),
            agency_name: org.type !== ORGANIZATION_TYPE.BRAND ? org.name : '',
            org_type: org.type,
            mobile: `${countryCode}${user.mobile}`,
            marketplace_region: store?.profile?.countryCode,
            utm_source: user.source,
            last_login: user.lastLogin,
            sync_progress: store.syncProgress,
            domain: window.location.hostname,
          },
        };

        if (!hasFCMToken(userId)) {
          setFCMTokenToLocal();
        }

        boot(config);
      } else {
        shutdown();
      }
      return () => {
        shutdown();
      };
    }
  }, [user._id]);

  if (!loading && redirect && history.location.pathname !== redirect) {
    history.push(redirect);
  }

  if (loading) return <PageLoader />;
  if (publicRoute) return children;
  if (accessDenied) return <AccessDenied />;

  if (!['growz', 'aisle'].includes(realm)) {
    return children;
  }
  return <SubscriptionCheck>{children}</SubscriptionCheck>;
}

export default AuthCheck;
